import React from "react";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";
import { useHistory } from "react-router-dom";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import "./markdown-assistant.css";

const Markdown = ({ children, components }) => {
  const history = useHistory();

  // Theme-aware table components
  const themedComponents = {
    // Custom link component that uses history.push
    a: ({ href, children, ...props }) => {
      const handleClick = (e) => {
        e.preventDefault();
        if (href) {
          history.push(href);
        }
      };

      return (
        <a
          href={href}
          onClick={handleClick}
          className="text-primary hover:text-primary/80 no-underline font-medium cursor-pointer hover:underline"
          {...props}
        >
          {children}
        </a>
      );
    },
    table: ({ children, ...props }) => (
      <TableContainer
        component={Paper}
        className="my-4 rounded-none border border-border bg-card"
        style={{
          boxShadow: "none",
          overflow: "hidden", // Keep overflow hidden but ensure borders are visible
        }}
        {...props}
      >
        <Table style={{ borderCollapse: "separate", borderSpacing: 0 }}>
          {children}
        </Table>
      </TableContainer>
    ),
    thead: ({ children, ...props }) => (
      <TableHead {...props}>{children}</TableHead>
    ),
    tbody: ({ children, ...props }) => (
      <TableBody {...props}>{children}</TableBody>
    ),
    tr: ({ children, node, ...props }) => {
      // Check if this is a header row by looking at the parent
      const isHeaderRow =
        node?.tagName === "tr" && node?.parent?.tagName === "thead";

      const TrComponent = ({ children, ...props }) => {
        const rowRef = React.useRef(null);
        const [isHovered, setIsHovered] = React.useState(false);
        const [isEven, setIsEven] = React.useState(false);
        const [isLastRow, setIsLastRow] = React.useState(false);

        React.useEffect(() => {
          if (rowRef.current && !isHeaderRow) {
            const parent = rowRef.current.closest("tbody");
            if (parent) {
              const rows = Array.from(parent.querySelectorAll("tr"));
              const index = rows.indexOf(rowRef.current);
              setIsEven(index % 2 === 1);
              setIsLastRow(index === rows.length - 1);
            }
          }
        }, [isHeaderRow]);

        return (
          <TableRow
            ref={rowRef}
            className={
              !isHeaderRow
                ? isEven
                  ? "transition-colors duration-200 bg-muted/50"
                  : "transition-colors duration-200"
                : ""
            }
            style={{
              ...(isHovered &&
                !isHeaderRow && {
                  backgroundColor: "hsl(var(--accent))",
                }),
            }}
            onMouseEnter={!isHeaderRow ? () => setIsHovered(true) : undefined}
            onMouseLeave={!isHeaderRow ? () => setIsHovered(false) : undefined}
            {...props}
          >
            {React.Children.map(children, (child, index) => {
              // Only remove borderBottom for ALL cells in the last data row, not header rows
              // NEVER modify header row cells (th or td in thead) - they must keep their borders
              if (!isHeaderRow && isLastRow && React.isValidElement(child)) {
                // Double check: only modify td cells that are NOT in thead
                // Check if the child is a th element or has component="th"
                const isThElement =
                  child.type === "th" ||
                  child.props?.component === "th" ||
                  (typeof child.type === "string" &&
                    child.type.toLowerCase() === "th");

                // Also check if it's a TableCell with component="th"
                const isHeaderCell =
                  isThElement ||
                  (child.type?.displayName === "TableCell" &&
                    child.props?.component === "th");

                // Only remove borderBottom for data cells in the last row
                if (!isThElement && !isHeaderCell) {
                  return React.cloneElement(child, {
                    style: {
                      ...(child.props?.style || {}),
                      borderBottom: 0,
                    },
                  });
                }
              }
              // For header rows or non-last rows, return as-is without modification
              return child;
            })}
          </TableRow>
        );
      };

      return <TrComponent {...props}>{children}</TrComponent>;
    },
    th: ({ children, ...props }) => {
      const isDark =
        typeof document !== "undefined" &&
        document.documentElement.classList.contains("dark");
      // Use TableCell with sx prop like the original code - this ensures styles are applied correctly
      return (
        <TableCell
          component="th"
          sx={{
            fontWeight: 700,
            fontSize: "0.875rem",
            textTransform: "uppercase",
            letterSpacing: "0.5px",
            backgroundColor: isDark ? "hsl(var(--muted))" : "hsl(var(--muted))",
            color: "hsl(var(--foreground))",
            borderBottom: "2px solid hsl(var(--primary)) !important",
            borderLeft: "none",
            borderRight: "none",
            borderTop: "none",
            padding: "16px 20px",
            position: "sticky",
            top: 0,
            zIndex: 1,
            userSelect: "none",
            cursor: "default",
          }}
          {...props}
        >
          {children}
        </TableCell>
      );
    },
    td: ({ children, node, ...props }) => {
      // Check if this td is actually in a thead (react-markdown might parse some headers as td)
      // If it's in thead, treat it as a header and use th styling
      const isInThead =
        node?.parent?.tagName === "thead" ||
        node?.parent?.parent?.tagName === "thead" ||
        node?.parent?.parent?.parent?.tagName === "thead";

      if (isInThead) {
        const isDark =
          typeof document !== "undefined" &&
          document.documentElement.classList.contains("dark");
        // Render as header with same styling as th component
        return (
          <TableCell
            component="th"
            sx={{
              fontWeight: 700,
              fontSize: "0.875rem",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              backgroundColor: isDark
                ? "hsl(var(--muted))"
                : "hsl(var(--muted))",
              color: "hsl(var(--foreground))",
              borderBottom: "2px solid hsl(var(--primary)) !important",
              borderLeft: "none",
              borderRight: "none",
              borderTop: "none",
              padding: "16px 20px",
              position: "sticky",
              top: 0,
              zIndex: 1,
              userSelect: "none",
              cursor: "default",
            }}
            {...props}
          >
            {children}
          </TableCell>
        );
      }

      // Otherwise, render as normal data cell
      return (
        <TableCell
          className="text-sm leading-[1.4] px-4 py-2 border-b border-border text-foreground"
          {...props}
        >
          {React.Children.map(children, (child) => {
            if (React.isValidElement(child) && child.type === "a") {
              return React.cloneElement(child, {
                className:
                  "font-medium no-underline text-primary hover:text-primary/80 hover:underline",
                style: {
                  ...(child.props?.style || {}),
                },
              });
            }
            return child;
          })}
        </TableCell>
      );
    },
    // Merge with any custom components passed in
    ...components,
  };

  return (
    <div className="markdown-assistant-container">
      <ReactMarkdown
        rehypePlugins={[rehypeRaw]} // Enables raw HTML
        remarkPlugins={[remarkGfm]} // Enables GitHub flavored markdown
        components={themedComponents}
      >
        {children}
      </ReactMarkdown>
    </div>
  );
};

export default Markdown;
